"use client";

import React, { useState, useEffect } from "react";
import { Menu, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { CartIcon } from "@/components/shop/CartIcon";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
function MenuItems() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent p-0 h-auto text-sm font-serif font-bold uppercase tracking-wider text-foreground/90 hover:text-primary data-[state=open]:text-primary transition-colors duration-300 focus:bg-transparent hover:bg-transparent border-none">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="min-w-[200px] bg-background border border-border/50 shadow-lg rounded-md p-2 animate-in fade-in zoom-in-95 duration-200">
                  <div className="flex flex-col gap-1">
                    {item.children.map((child, j) => <NavigationMenuLink key={child.id} asChild>
                        <Link to={child.href || "#"} className="block px-4 py-2 text-xs font-sans font-semibold uppercase tracking-widest text-foreground/70 hover:text-primary hover:bg-primary/5 rounded-md transition-all">
                          {child.label}
                        </Link>
                      </NavigationMenuLink>)}
                  </div>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="text-sm font-serif font-bold uppercase tracking-wider text-foreground/90 hover:text-primary transition-colors duration-300">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function StaticHeaderMenu() {
  return <MenuProvider menu_id="23">
      <MenuItems />
    </MenuProvider>;
}
function MobileMenuContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const toggleMenu = (id: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  if (loading) {
    return <div className="flex flex-col gap-4 py-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-4 py-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col border-b border-border/10 pb-2">
          {item.children && item.children.length > 0 ? <>
              <Button variant="ghost" onClick={() => toggleMenu(item.id)} className="flex items-center justify-between w-full text-2xl font-serif font-bold uppercase tracking-tight text-foreground hover:text-primary transition-colors duration-300 p-0 h-auto bg-transparent hover:bg-transparent border-none">
                <span>{item.label}</span>
                <ChevronDown className={`size-6 transition-transform duration-300 ${openMenus[item.id] ? "rotate-180" : ""}`} />
              </Button>
              <div className={`grid transition-all duration-300 ease-in-out ${openMenus[item.id] ? "grid-rows-[1fr] opacity-100 mt-4" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden flex flex-col gap-3 pl-4">
                  {item.children.map(child => <Link key={child.id} to={child.href || "#"} className="text-lg font-sans font-medium text-muted-foreground hover:text-primary transition-colors">
                      {child.label}
                    </Link>)}
                </div>
              </div>
            </> : <Link to={item.href || "#"} className="text-2xl font-serif font-bold uppercase tracking-tight text-foreground hover:text-primary transition-colors duration-300">
              {item.label}
            </Link>}
        </div>)}
    </nav>;
}
function MobileMenuConsumer() {
  return <MenuProvider menu_id="23">
      <MobileMenuContent />
    </MenuProvider>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <header id="studio-header" className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${isScrolled ? "bg-background/95 backdrop-blur-md border-border/50 shadow-md" : "bg-background border-transparent"}`}>
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        <div className="flex-shrink-0">
          <Link to="/" className="flex items-center">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        <div className="hidden md:flex flex-1 justify-center">
          <StaticHeaderMenu />
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <div className="relative">
            <CartIcon variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-primary/5 transition-colors rounded-lg" iconSize="size-5" />
          </div>

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden border-2 border-primary text-primary hover:bg-primary/5 rounded-lg" aria-label="Open menu">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background border-l border-border/50">
              <SheetHeader className="text-left border-b border-border/10 pb-4">
                <SheetTitle className="flex items-center">
                  <WvcLogo className="h-6 w-auto" />
                </SheetTitle>
              </SheetHeader>
              
              <MobileMenuConsumer />
              
              <div className="mt-auto pt-8 border-t border-border/10">
                <p className="text-xs font-sans text-muted-foreground uppercase tracking-widest">
                  Photo Glamour Studio
                </p>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
}